import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { fetchVendorOptions, DEFAULT_VENDOR_OPTIONS } from "@/lib/expenseOptions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Receipt, ArrowLeft, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ReceiptUploader } from "@/components/ReceiptUploader";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Drivers see a focused subset of categories — most field-relevant items first.
const DRIVER_CATEGORIES = [
  "Gas", "Vehicle maintenance", "Supplies", "Tolls & parking", "Other",
];

export default function DriverExpenses() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get(`/expenses`)
      .then((r) => setList(r.data))
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const total = useMemo(() => list.reduce((s, e) => s + Number(e.amount || 0), 0), [list]);

  const remove = async (e) => {
    if (!window.confirm(`Delete this expense (${fmtMoney(e.amount)} · ${e.category})?`)) return;
    try { await api.delete(`/expenses/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/driver" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-driver-day">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to today
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <Wallet className="w-6 h-6 text-primary" />
          <h1 className="font-display text-2xl font-bold tracking-tight">My expenses</h1>
        </div>
        <p className="text-sm text-muted-foreground">Submit gas receipts and other costs. Only you and the office can see what you submit here.</p>

        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{list.length} entries · total <span className="font-semibold text-foreground mono-num">{fmtMoney(total)}</span></p>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-sm" data-testid="driver-expense-add">
            <Plus className="w-4 h-4 mr-2" /> Add expense
          </Button>
        </div>

        <div className="border border-border rounded-sm divide-y divide-border">
          {list.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              No expenses submitted yet. Tap <strong>Add expense</strong> after each gas fill-up so the office reimburses you correctly.
            </div>
          )}
          {list.map((e) => {
            const receiptCount = typeof e.receipt_count === "number"
              ? e.receipt_count
              : (e.receipts?.length || 0) + (e.receipt_data_url ? 1 : 0);
            return (
              <div key={e.id} className="px-4 py-3" data-testid={`driver-expense-row-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold">{e.category}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{e.date}</span>
                    </div>
                    {e.vendor && <div className="text-xs text-muted-foreground mt-0.5">{e.vendor}</div>}
                    {e.notes && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{e.notes}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                      {receiptCount > 0
                        ? <span className="text-primary font-semibold uppercase tracking-wider">📎 {receiptCount} receipt{receiptCount > 1 ? "s" : ""}</span>
                        : e.no_receipt_reason
                          ? <span className="text-amber-700 font-semibold uppercase tracking-wider" title={e.no_receipt_reason}>⚠ no receipt</span>
                          : null}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="mono-num font-bold">{fmtMoney(e.amount)}</div>
                    <div className="flex justify-end gap-1 mt-1">
                      <Button size="sm" variant="ghost" onClick={async () => {
                        // Receipts are lazy-loaded to keep the list snappy —
                        // pull the full doc before opening the edit form.
                        let full = e;
                        if (!Array.isArray(e.receipts) && !e.receipt_data_url) {
                          try { const { data } = await api.get(`/expenses/${e.id}`); full = { ...e, ...data }; }
                          catch (_) { /* keep row as-is; form tolerates empty receipts */ }
                        }
                        setEditing(full); setOpen(true);
                      }} className="rounded-sm h-7 w-7 p-0" data-testid={`driver-expense-edit-${e.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e)} className="rounded-sm h-7 w-7 p-0 text-destructive" data-testid={`driver-expense-delete-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DriverExpenseDialog
          open={open}
          onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}
          editing={editing}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      </div>
    </div>
  );
}

function DriverExpenseDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ date: todayISO(), category: "Gas", amount: 0, vendor: "", notes: "", receipts: [], no_receipt_reason: "" });
  const [noReceipt, setNoReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendorOptions, setVendorOptions] = useState(DEFAULT_VENDOR_OPTIONS);

  useEffect(() => {
    if (open) fetchVendorOptions(api, "pool").then(setVendorOptions);
  }, [open]);

  useEffect(() => {
    if (open) {
      const base = editing
        ? {
            date: editing.date,
            category: editing.category,
            amount: editing.amount,
            vendor: editing.vendor || "",
            notes: editing.notes || "",
            receipts: [
              ...(Array.isArray(editing.receipts) ? editing.receipts : []),
              ...(editing.receipt_data_url ? [editing.receipt_data_url] : []),
            ],
            no_receipt_reason: editing.no_receipt_reason || "",
          }
        : { date: todayISO(), category: "Gas", amount: 0, vendor: "", notes: "", receipts: [], no_receipt_reason: "" };
      setForm(base);
      setNoReceipt(!!base.no_receipt_reason && base.receipts.length === 0);
    }
  }, [open, editing]);

  const save = async () => {
    if (Number(form.amount) <= 0) return toast.error("Amount must be > 0");
    if (!(form.vendor || "").trim()) return toast.error("Vendor / store is required.");
    if (noReceipt) {
      if (!form.no_receipt_reason.trim()) return toast.error("Please explain why no receipt is available.");
    } else if (form.receipts.length === 0) {
      return toast.error("Snap a photo of your receipt, or tick 'No receipt' below.");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        receipts: noReceipt ? [] : form.receipts,
        no_receipt_reason: noReceipt ? form.no_receipt_reason.trim() : "",
        receipt_data_url: "",
      };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post(`/expenses`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-expense-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit expense" : "Add expense"}</DialogTitle>
          <DialogDescription>Snap your gas/maintenance receipt and submit so the office can reimburse you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="driver-expense-date" /></div>
            <div><Label>Amount ($)</Label><Input type="number" step="0.01" min="0" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} data-testid="driver-expense-amount" /></div>
          </div>
          <div>
            <Label>Category</Label>
            <Combobox
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={DRIVER_CATEGORIES}
              placeholder="Pick or type…"
              testId="driver-expense-category"
            />
          </div>
          <div>
            <Label>Vendor *</Label>
            <Combobox
              value={form.vendor}
              onChange={(v) => setForm({ ...form, vendor: v })}
              options={vendorOptions}
              placeholder="e.g. Shell, Sunoco…"
              testId="driver-expense-vendor"
            />
          </div>
          <div><Label>Notes (optional)</Label><Textarea rows={2} placeholder="e.g. truck #2, before route" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="driver-expense-notes" /></div>

          {!noReceipt ? (
            <div className="border border-border rounded-sm p-3 bg-secondary/20">
              <ReceiptUploader
                value={form.receipts}
                onChange={(v) => setForm({ ...form, receipts: v })}
                testId="driver-expense-receipt"
              />
            </div>
          ) : (
            <div>
              <Label>Why no receipt? <span className="text-destructive">*</span></Label>
              <Textarea rows={2} placeholder="e.g. pump didn't print one, lost it…" value={form.no_receipt_reason} onChange={(e) => setForm({ ...form, no_receipt_reason: e.target.value })} data-testid="driver-expense-no-receipt-reason" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={noReceipt} onChange={(e) => setNoReceipt(e.target.checked)} data-testid="driver-expense-no-receipt-toggle" />
            <span>No receipt available</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="driver-expense-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
